import { useState, useEffect } from 'react';

import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { useUserContext } from 'contexts/UserContext';
import { useVerificationContext } from 'contexts/VerificationContext';
import { useOnUpdateOnly } from 'hooks/UseOnUpdateOnly';

export interface ShowPhoneNumberButtonProps {
  onReveal: () => Promise<string | false>;
  onCall?: () => void;
  showNumberTextVariant?: boolean;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  trackingId?: string;
  isPrimaryButton: boolean;
  adId: number;
  disabled?: boolean;
  setLoginAndPhoneReveal: (t: boolean) => void;
  loginAndPhoneReveal: boolean;
}

function ShowPhoneNumberButton({
  onReveal,
  showNumberTextVariant,
  size,
  trackingId,
  onCall,
  isPrimaryButton,
  adId,
  disabled,
  setLoginAndPhoneReveal,
  loginAndPhoneReveal,
}: ShowPhoneNumberButtonProps) {
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, isLoginModalOpen } = useUserContext();
  const { triggerAfterVerify, setTriggerAfterVerify } =
    useVerificationContext();

  useOnUpdateOnly(() => {
    setPhone('');
  }, [adId]);

  async function handleReveal() {
    if (phone) {
      if (onCall) {
        onCall();
        return;
      } else {
        // If the phone is already available and we are
        // not wanting to do anything on the call then return
        return;
      }
    } else {
      setIsSubmitting(true);
      const phoneNumber = await onReveal();
      setIsSubmitting(false);
      if (phoneNumber) {
        setPhone(phoneNumber);
      }
    }
  }

  useEffect(() => {
    if (user && loginAndPhoneReveal) {
      handleReveal();
      setLoginAndPhoneReveal(false);
    }
  }, [isLoginModalOpen]);

  useEffect(() => {
    if (triggerAfterVerify && user) {
      setTriggerAfterVerify(false);
      handleReveal();
    }
  }, [triggerAfterVerify]);

  return (
    <ButtonLink
      size={size}
      ofType={isPrimaryButton ? 'PRIMARY' : 'SECONDARY'}
      data-testid={phone ? 'call-phone-number' : 'view-phone-number'}
      href={phone ? `tel:${phone}` : undefined}
      onClick={handleReveal}
      data-tracking={trackingId}
      isFakeLink={phone ? false : true}
      disabled={disabled}
    >
      {phone && !isSubmitting
        ? `Call ${phone}`
        : isSubmitting
        ? 'Loading'
        : showNumberTextVariant
        ? 'Show Number'
        : 'Show Phone Number'}
    </ButtonLink>
  );
}

export { ShowPhoneNumberButton };
